import { useState } from 'react';
import type { TFunction } from 'i18next';
import { toast } from 'sonner';
import type { QueryClient } from '@tanstack/react-query';
import { useApiAdminsalesHierarchyLinksIdDelete } from '@r/api/generated/client.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import type { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { invalidateSalesHierarchyTreeQueries } from '@admin/components/hierarchy/hierarchy-utils.ts';
import type { HierarchyFlowNode } from '@admin/components/hierarchy/sales-hierarchy-flow-utils.ts';
import type { HierarchyRelationCascadeChoice } from '@admin/components/hierarchy/hierarchy-relation-cascade-dialog.tsx';

interface UseHierarchyRelationActionsParams {
  t: TFunction;
  queryClient: QueryClient;
  hierarchyId: number;
  showOrvalErrorToast: ReturnType<typeof useOrvalErrorToast>;
  allNodes: HierarchyFlowNode[];
  setStagedNodes: (updater: (snapshot: HierarchyFlowNode[]) => HierarchyFlowNode[]) => void;
}

/**
 * Flux complet de suppression/détachement d'une relation (corbeille/ciseaux) :
 * confirmation (toast simple ou choix cascade via Dialog selon que la cible a
 * une équipe), appel `DELETE`, et repositionnement local des nœuds concernés
 * une fois la réponse connue. Extrait de `SalesHierarchyCanvas` (composant
 * devenu trop volumineux) — regroupe un seul concept cohérent de bout en bout.
 */
export function useHierarchyRelationActions({
  t,
  queryClient,
  hierarchyId,
  showOrvalErrorToast,
  allNodes,
  setStagedNodes
}: UseHierarchyRelationActionsParams) {
  const [deletingLinkId, setDeletingLinkId] = useState<number | null>(null);
  /** Choix cascade en attente (cible avec sa propre équipe) — une vraie Dialog, pas un toast : texte trop long pour tenir dans un encart coin d'écran. */
  const [cascadeChoice, setCascadeChoice] = useState<HierarchyRelationCascadeChoice | null>(null);

  const deleteLinkMutation = useApiAdminsalesHierarchyLinksIdDelete({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.hierarchy.link.delete.success'));
        invalidateSalesHierarchyTreeQueries(queryClient, hierarchyId);
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'delete', '/admin/sales-hierarchy-links')) {
          showOrvalErrorToast(error, t('admin.hierarchy.link.delete.error'));
        } else {
          toast.error(t('admin.hierarchy.link.delete.error'));
        }
      },
      onSettled: () => setDeletingLinkId(null)
    }
  });

  /**
   * Exécute la suppression du lien une fois confirmée (et le choix cascade
   * connu). "trash" : le nœud cible disparaît du canvas — rien à faire de
   * plus, il n'aura plus aucun lien nulle part, il disparaîtra tout seul au
   * prochain refetch. "cut" : le nœud cible doit rester visible, orphelin.
   *   - cascade=false : le back réattache ses subordonnés directs au
   *     grand-parent (liens intacts, juste déplacés) — ils réapparaîtront
   *     tout seuls au refetch, sous le grand-parent. Seule la cible a besoin
   *     d'être reposée localement.
   *   - cascade=true : le back détruit tous les liens du sous-arbre, chaque
   *     membre devient réellement indépendant ("tous sans relation") — on
   *     les repose tous localement, chacun orphelin.
   */
  function performRelationDelete(
    mode: 'trash' | 'cut',
    linkId: number,
    target: string,
    cascade: boolean
  ) {
    setDeletingLinkId(linkId);
    deleteLinkMutation.mutate(
      { id: String(linkId), params: { cascade } },
      {
        onSuccess: () => {
          if (mode === 'trash') return;

          if (!cascade) {
            setStagedNodes((snapshot) => {
              if (snapshot.some((node) => node.id === target)) return snapshot;
              const targetNode = allNodes.find((node) => node.id === target);
              return targetNode ? [...snapshot, { ...targetNode, parentId: null }] : snapshot;
            });
            return;
          }

          const subtreeIds = new Set([target]);
          let grew = true;
          while (grew) {
            grew = false;
            for (const node of allNodes) {
              if (node.parentId && subtreeIds.has(node.parentId) && !subtreeIds.has(node.id)) {
                subtreeIds.add(node.id);
                grew = true;
              }
            }
          }

          setStagedNodes((snapshot) => {
            const relevant = allNodes.filter((node) => subtreeIds.has(node.id));
            const additions = relevant.filter(
              (node) => !snapshot.some((staged) => staged.id === node.id)
            );
            return [...snapshot, ...additions.map((node) => ({ ...node, parentId: null }))];
          });
        }
      }
    );
  }

  /**
   * Demande confirmation avant de couper/supprimer un lien — même flux
   * qu'on vienne d'un nœud standalone ou d'une ligne dans un cluster.
   * Redemande le choix cascade uniquement si la cible a elle-même des
   * subordonnés (sinon la question n'a pas de sens, rien à cascader).
   */
  function confirmRelationAction(mode: 'trash' | 'cut', linkId: number, target: string) {
    const targetNode = allNodes.find((node) => node.id === target);
    const name = targetNode?.label ?? target;
    const hasChildren = allNodes.some((node) => node.parentId === target);

    if (!hasChildren) {
      const confirmKey =
        mode === 'trash'
          ? 'admin.hierarchy.relation.trash.confirm'
          : 'admin.hierarchy.relation.cut.confirm';
      toast(t(confirmKey, { name }), {
        action: {
          label: t('label.confirm'),
          onClick: () => performRelationDelete(mode, linkId, target, false)
        },
        cancel: { label: t('label.cancel'), onClick: () => {} }
      });
      return;
    }

    setCascadeChoice({ mode, linkId, target, name });
  }

  return {
    deletingLinkId,
    cascadeChoice,
    confirmRelationAction,
    confirmCascadeChoice: (cascade: boolean) => {
      if (cascadeChoice) {
        performRelationDelete(
          cascadeChoice.mode,
          cascadeChoice.linkId,
          cascadeChoice.target,
          cascade
        );
      }
      setCascadeChoice(null);
    },
    cancelCascadeChoice: () => setCascadeChoice(null)
  };
}
